import { IResponse, consumer } from "@bemap/utils"

// Types based on the backend entities
export interface CourseCertificate {
  id: number;
  userId: number;
  courseId: number;
  issueDate: string;
  expirationDate: string;
  status: 'waiting' | 'generated' | 'cancelled';
  hash: string;
  key?: string;
  grade?: number;
  completionPercentage: number;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: number;
    name: string;
    email: string;
  };
  course?: {
    id: number;
    title: string;
    instructor: string;
  };
}

export interface CompletionStats {
  totalEnrollments: number;
  completedCourses: number;
  certificatesIssued: number;
  completionRate: number;
}

// API calls
export const getAllCertificates = async (): Promise<IResponse> => {
  const url = '/course-certificates';

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getCertificatesByCourse = async (courseId: number): Promise<IResponse> => {
  const url = `/course-certificates?courseId=${courseId}`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getCertificatesByUser = async (userId: number): Promise<IResponse> => {
  const url = `/course-certificates/user/${userId}`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getCertificateById = async (id: number): Promise<IResponse> => {
  const url = `/course-certificates/${id}`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const generateCertificate = async (id: number): Promise<IResponse> => {
  const url = `/course-certificates/${id}/generate`;

  try {
    return await consumer('POST', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const cancelCertificate = async (id: number): Promise<IResponse> => {
  const url = `/course-certificates/${id}/cancel`;

  try {
    return await consumer('POST', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const deleteCertificate = async (id: number): Promise<IResponse> => {
  const url = `/course-certificates/${id}`;

  try {
    return await consumer('DELETE', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const requestCertificate = async (courseId: number, userId?: number): Promise<IResponse> => {
  const url = userId
    ? `/course-certificates/request/${courseId}?userId=${userId}`
    : `/course-certificates/request/${courseId}`;

  try {
    return await consumer('POST', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const downloadCertificate = async (id: number): Promise<string> => {
  const url = `/course-certificates/${id}/download`;

  try {
    const response = await consumer('GET', url);
    return response.message; // This should be the download URL
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getCompletionStats = async (): Promise<IResponse> => {
  const url = '/course-certificates/stats/completion';

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
}; 